import { useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import Header from './Header'
import Footer from './Footer'
import SuspendedPage from './SuspendedPage'
import DeletedAccountPage from './DeletedAccountPage'

export default function Layout() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Ping last_seen_at when user is active (once per load; used in User management "Last active")
  useEffect(() => {
    if (!user?.id) return
    supabase.rpc('update_my_last_seen').then(() => {})
  }, [user?.id])

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile-suspended-check', user?.id],
    queryFn: async () => {
      if (!user) return null
      const { data, error } = await supabase
        .from('profiles')
        .select('is_suspended, status, must_change_password')
        .eq('id', user.id)
        .maybeSingle()
      if (error) return null
      return data as { is_suspended: boolean; status: string | null; must_change_password?: boolean } | null
    },
    enabled: !!user,
    staleTime: 60_000,
  })

  if (user && !profileLoading && profile?.status === 'soft_deleted') {
    return <DeletedAccountPage signOut={signOut} />
  }

  if (user && !profileLoading && profile?.is_suspended) {
    return <SuspendedPage signOut={signOut} />
  }

  if (user && !profileLoading && profile?.must_change_password && location.pathname !== '/set-password') {
    navigate('/set-password', { replace: true })
    return null
  }

  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main" className="skip-link">
        Skip to main content
      </a>
      <Header />
      <main id="main" className="flex-1 container mx-auto px-4 py-8" tabIndex={-1}>
        <Outlet />
      </main>
      <Footer />
    </div>
  )
}

